import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { LegalHeader, Section, Intro } from "@/pages/Privacy";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <LegalHeader
        eyebrow="Legal"
        title="Terms & Conditions"
        updated="February 1, 2026"
      />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="terms-page">
        <div className="prose prose-slate max-w-none">
          <Intro>
            Welcome to SELLEXR. These Terms &amp; Conditions ("Terms") govern your access to and use of the SELLEXR website, dashboards, mobile applications, and services (collectively, the "Platform"). By creating an account or otherwise using the Platform, you agree to these Terms.
          </Intro>

          <Section title="1. About SELLEXR">
            <p>SELLEXR operates a two-sided marketplace that connects local shopkeepers ("Shopkeepers") with certified freelancers ("Freelancers") who help manage online stores on Amazon, Flipkart, Meesho and other marketplaces. SELLEXR is a facilitator; the services delivered by Freelancers are provided by them directly to Shopkeepers.</p>
          </Section>

          <Section title="2. Eligibility">
            <p>You must be at least 18 years old and legally capable of entering into a binding contract to use the Platform. By using SELLEXR, you represent that you meet this requirement and that the information you provide is accurate and complete.</p>
          </Section>

          <Section title="3. Accounts">
            <ul>
              <li>You are responsible for maintaining the confidentiality of your credentials.</li>
              <li>Freelancer accounts require admin approval before login is enabled.</li>
              <li>You must not share your account, impersonate another person, or misrepresent your skills or business.</li>
              <li>We may suspend or terminate accounts that violate these Terms.</li>
            </ul>
          </Section>

          <Section title="4. Roles &amp; Responsibilities">
            <p><strong>Shopkeepers</strong> agree to:</p>
            <ul>
              <li>Provide accurate store, marketplace, and product information.</li>
              <li>Pay Freelancers the agreed compensation on time.</li>
              <li>Treat Freelancers professionally and rate them fairly.</li>
            </ul>
            <p><strong>Freelancers</strong> agree to:</p>
            <ul>
              <li>Complete SELLEXR Academy training required for their skills.</li>
              <li>Execute assigned tasks honestly, on time, and to the best of their ability.</li>
              <li>Never engage in fraud, listing manipulation, review manipulation, or any activity that violates a marketplace's policies.</li>
            </ul>
          </Section>

          <Section title="5. Payments">
            <p>The MVP of SELLEXR uses a mock/manual payment ledger. Shopkeepers record payments to Freelancers directly through the dashboard. When we introduce integrated payments (Stripe, Razorpay, or similar), additional payment terms will apply. SELLEXR may charge platform fees, which will be disclosed transparently before you agree to them.</p>
          </Section>

          <Section title="6. Training &amp; Certification">
            <p>SELLEXR Academy provides training and quizzes. A minimum quiz score of 60% is required to earn a certificate. Certificates are non-transferable and may be revoked for misconduct or fraud.</p>
          </Section>

          <Section title="7. AI Client Matching">
            <p>Our AI matching feature ranks Freelancers using their profile, skills, ratings, and stated availability. The AI provides recommendations only; Shopkeepers remain solely responsible for their hiring decisions.</p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>The SELLEXR name, logo, wordmark, tagline, dashboards, training materials, and platform code are the property of SELLEXR. You may not copy, reproduce, or distribute them without written permission. Content you create (product listings, images, task descriptions) remains yours; you grant SELLEXR a non-exclusive licence to store, display, and process it as needed to operate the Platform.</p>
          </Section>

          <Section title="9. Prohibited Conduct">
            <ul>
              <li>Uploading unlawful, infringing, or harmful content.</li>
              <li>Attempting to access other users' accounts or data.</li>
              <li>Reverse-engineering, scraping, or interfering with the Platform.</li>
              <li>Using the Platform to violate any marketplace terms (Amazon, Flipkart, Meesho, etc.).</li>
            </ul>
          </Section>

          <Section title="10. Disclaimers">
            <p>SELLEXR is provided on an "as is" and "as available" basis. We make no warranties regarding uninterrupted service, error-free operation, or specific business outcomes. Freelancer services are provided by independent third parties; SELLEXR does not guarantee their quality, accuracy, or results.</p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>To the maximum extent permitted by law, SELLEXR and its affiliates will not be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenues, data, or goodwill. Our aggregate liability arising out of or related to the Platform will not exceed the fees you paid to SELLEXR in the twelve months preceding the claim, or ₹5,000, whichever is greater.</p>
          </Section>

          <Section title="12. Indemnification">
            <p>You agree to indemnify and hold harmless SELLEXR, its officers, employees, and partners from any claims arising out of your breach of these Terms, your content, or your use of the Platform.</p>
          </Section>

          <Section title="13. Termination">
            <p>You may close your account at any time by contacting us. We may suspend or terminate your access if you violate these Terms, engage in fraud, or if we discontinue the service. Provisions relating to intellectual property, disclaimers, limitation of liability, and governing law survive termination.</p>
          </Section>

          <Section title="14. Governing Law &amp; Disputes">
            <p>These Terms are governed by the laws of India. Any dispute arising out of or in connection with these Terms will be subject to the exclusive jurisdiction of the courts located in Bengaluru, Karnataka, India.</p>
          </Section>

          <Section title="15. Changes">
            <p>We may update these Terms from time to time. Material changes will be notified in-app or by email. Continued use of the Platform after changes take effect constitutes acceptance of the revised Terms.</p>
          </Section>

          <Section title="16. Contact">
            <p>Questions about these Terms? Email <a href="mailto:hello@sellexr.com" className="text-brand">hello@sellexr.com</a>.</p>
          </Section>

          <div className="mt-12 pt-8 border-t border-slate-200 text-sm text-slate-500">
            See also our <Link to="/privacy" className="text-brand font-medium">Privacy Policy</Link>.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
